import type {PackageResponseBody, TrimResponseBody} from '@dms/api/new-vehicle';

import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import {isInPricelist} from './isInPricelist';
import {offeredTrimChanges} from './offeredTrimChanges';
import {pricedItemGroupKeys} from './pricedItemGroupKeys';

type Trim = Pick<TrimResponseBody, 'id' | 'name' | 'publicationStatus'>;

/** The rows of an active package on online trims leave the price list; a draft package has none. */
const archivedRows = (item: PackageResponseBody, trims: Trim[]) =>
  offeredTrimChanges({
    resourceType: 'packageTrim',
    trims,
    stored: {name: item.name, rows: item.trims, isMember: isInPricelist.package(item.status)},
    next: {name: item.name, rows: [], isMember: false},
  });

/**
 * The rows of a package archive and its entry for an open group. The entry reads the package when
 * the group is confirmed and sends that version and lists those rows, so a write to the package
 * outside the group does not make the batch stale. Its name is free in the batch after it.
 */
export const packageArchiveChange = (
  row: PackageResponseBody,
  trims: Trim[],
  readPackage: (packageId: string) => Promise<PackageResponseBody>
) => ({
  items: archivedRows(row, trims),
  entry: {
    key: pricedItemGroupKeys.package(row.id),
    title: row.name,
    usedValues: pricedItemGroupKeys.usedValue('package', 'name', row.name),
    build: () =>
      readPackage(row.id).then((stored) => ({
        items: archivedRows(stored, trims),
        operations: [
          {type: 'package.archive' as const, targetId: row.id, body: {version: stored.version}},
        ],
      })),
  } satisfies Omit<PricedItemBatchEntry, 'items'>,
});
